import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import styles from "./GrowthSection.module.css";
import templatePt from "../../data/pt/growth_templates.json";
import templateEn from "../../data/en/growth_templates.json";
import { renderText, renderArray } from "../../utils/templateRenderer";

/**
 * GrowthSection (Crescimento Pessoal)
 * Props:
 *  - premiumPayload
 *  - insights
 *  - derivedArchetype
 *  - userData
 *  - compact
 */

function fallbackGrowth({ temperament }) {
  const [E, A, S] = temperament || [50, 50, 50];
  return {
    intro: `Seu crescimento pessoal combina Emotionality=${E}%, Activity=${A}%, Sociability=${S}%.`,
    strengths: [
      "Capacidade de reflexão",
      "Potencial de aprendizagem relacional",
    ],
    challenges: [
      "Sustentar prática diária",
      "Ruminação em momentos de estresse",
    ],
    practices: ["Registro diário 5 min", "Revisão semanal 5 min"],
    exercises: [
      { title: "Registro", description: "Escreva 3 aprendizados do dia." },
    ],
    reflectionPrompts: ["O que eu aprendi?"],
    hints: [],
  };
}

export default function GrowthSection({
  premiumPayload = null,
  insights = null,
  derivedArchetype = null,
  userData = null,
  wrapPremium = null,
}) {
  const { i18n, t } = useTranslation("user");
  const template = i18n.language?.startsWith("pt") ? templatePt : templateEn;
  const archeKey = useMemo(() => {
    const candidate =
      (premiumPayload && premiumPayload.archetypeKey) ||
      (derivedArchetype && (derivedArchetype.key || derivedArchetype.label)) ||
      "default";
    return String(candidate).toLowerCase().replace(/\s+/g, "-");
  }, [premiumPayload, derivedArchetype]);

  const context = useMemo(
    () => ({
      userName:
        (userData && userData.name) ||
        (premiumPayload && premiumPayload.name) ||
        "Usuário",
      meta: (template[archeKey] && template[archeKey].meta) || {},
      personalityTraits: (premiumPayload &&
        premiumPayload.personalityTraits) || [50, 50, 50],
      characterTraits: (premiumPayload && premiumPayload.characterTraits) || {
        discipline: 50,
        mentalOpenness: 50,
        emotionalOpenness: 50,
        perspective: 50,
      },
      chosenCharacter: (premiumPayload && premiumPayload.chosenCharacter) || {},
      insights: insights || {},
    }),
    [premiumPayload, insights, derivedArchetype, userData, archeKey]
  );

  const tpl = template[archeKey] || template["default"] || null;

  const assembled = useMemo(() => {
    if (!tpl || !tpl.growth) {
      return fallbackGrowth({
        temperament: context.personalityTraits.slice(0, 3),
      });
    }
    const g = tpl.growth;
    const intro = renderText(g.intro || "", context);
    const strengths = renderArray(g.strengths || [], context);
    const challenges = renderArray(g.challenges || [], context);
    const practices = renderArray(g.practices || [], context);
    const exercises = Array.isArray(g.exercises)
      ? g.exercises.map((ex) => ({
          title: renderText(ex.title || "", context),
          description: renderText(ex.description || "", context),
        }))
      : [];
    const reflectionPrompts = renderArray(g.reflectionPrompts || [], context);
    const hints = renderArray(g.hints || [], context);
    return {
      intro,
      strengths,
      challenges,
      practices,
      exercises,
      reflectionPrompts,
      hints,
    };
  }, [tpl, context]);

  return (
    <section className={styles.growthSection}>
      <header className={styles.header}>
        <h3>{t("profile.growthTitle")}</h3>
      </header>
      <div className={styles.grid}>
        <div className={styles.col}>
          <h4>{t("profile.growthSubtitle")}</h4>
          <p className={styles.lead}>{assembled.intro}</p>

          <h5>{t("profile.growthPoints")}</h5>
          <ul className={styles.points}>
            {assembled.strengths.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
          <h5>{t("profile.growthDangerPoints")}</h5>
          <ul className={styles.pointsDanger}>
            {assembled.challenges.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>

        <div className={styles.col}>
          <h5 style={{ marginTop: 12 }}>{t("profile.growthRecomendations")}</h5>
          {wrapPremium(
            <div>
              <ul className={styles.points}>
                {assembled.practices.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          <h5 style={{ marginTop: 12 }}>{t("profile.growthExercises")}</h5>
          {wrapPremium(
            assembled.exercises && assembled.exercises.length > 0 && (
              <>
                <div className={styles.exList}>
                  {assembled.exercises.map((ex, i) => (
                    <div key={i} className={styles.exItem}>
                      <strong>{ex.title}</strong>
                      <div className={styles.exDesc}>{ex.description}</div>
                    </div>
                  ))}
                </div>
              </>
            )
          )}
        </div>
      </div>

      <div className={styles.bottom}>
        <div>
          <h5>{t("profile.growthReflection")}</h5>
          <ul className={styles.points}>
            {assembled.reflectionPrompts.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </div>

        {assembled.hints && assembled.hints.length > 0 && (
          <aside className={styles.sideHints}>
            <h5>{t("profile.growthHints")}</h5>
            <div className={styles.hints}>
              {assembled.hints.map((h, i) => (
                <span key={i} className={styles.hintBadge}>
                  {h}
                </span>
              ))}
            </div>
          </aside>
        )}
      </div>
    </section>
  );
}
